/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractFormComponent, CommonActions, FormActionPipelines, FormFlowState } from '@gh/builder-shared';
import { ActionFlow, getDialogInitData } from '@gh/common-shared';
import {
  DialogInput,
  FileInvalidEvent,
  InvalidFileReason,
  NotificationService,
  UploadContext,
} from '@gh/core-ui';
import { assign } from 'lodash';
import {
  UserProfilePhoto,
  UserProfilePhotoBusinessService,
  UserProfilePhotoDialogConfig,
} from '../../resources';
import { UserProfilePhotoFormService } from './user-profile-photo-form.service';
import { ImageCroppedEvent } from 'ngx-image-cropper';

@Component({
  selector: 'gh-user-profile-photo-dialog',
  template: require('./user-profile-photo-dialog.component.html'),
  styles: [require('./user-profile-photo-dialog.component.scss')],
  viewProviders: [
    UploadContext,
    UserProfilePhotoFormService,
  ],
})
export class UserProfilePhotoDialogComponent extends AbstractFormComponent<UserProfilePhoto>
  implements OnInit, DialogInput<UserProfilePhotoDialogConfig> {
  input: UserProfilePhotoDialogConfig;
  imageChangedEvent: any = null;
  croppedImage: any = null;
  photo: any;
  uploading: boolean;
  initialImageUrl: string = '';
  coordinates: any = {
    xLeft: 0,
    yTop: 0,
    xRight: 0,
    yBottom: 0,
  };
  saveCropperCoords: any = {
    xLeft: 0,
    yTop: 0,
    xRight: 0,
    yBottom: 0,
  };
 cropperCoords: any = {
   x1: 0,
   x2: 0,
   y1: 0,
   y2: 0,
 };

  constructor(
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    public form: UserProfilePhotoFormService,
    public dialogRef: MatDialogRef<UserProfilePhotoDialogComponent>,
    private notificationService: NotificationService,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private businessService: UserProfilePhotoBusinessService,
  ) {
    super();
  }

  ngOnInit(): void {
    const { photo, ...init } = getDialogInitData(this.dialogData);

    assign(this, init);
    this.photo = photo;
    this.form.build({ picture: photo });
    if (photo) {
      this.initialImageUrl = photo.location;
    }
  }

  get completeFlow(): ActionFlow<FormFlowState<UserProfilePhoto>> {
    return this.pipelines.complete({
     execute: this.commonActions.execute((state) =>
       this.businessService.savePhoto(<any>state.value['picture'], this.coordinates, this.saveCropperCoords)),
     finish: (state: FormFlowState<UserProfilePhoto>) => this.dialogRef.close(state.form),
   });
  }

  onFileInvalid(event: FileInvalidEvent): void {
    this.notificationService.error({
     [InvalidFileReason.TooLarge]: 'msg_item_picture_not_correct',
     [InvalidFileReason.NotAccepted]: 'msg_item_picture_not_accepted',
     [InvalidFileReason.WrongSize]: 'msg_profile_picture_resolution',
   }[event.reason]);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  fileChangeEvent(event: any): void {
    if (event) {
      this.photo = event;
      this.form.build({ picture: event });
      this.imageChangedEvent = event.blob;
      this.initialImageUrl = '';
    } else {
      this.imageChangedEvent = null;
      this.initialImageUrl = '';
      this.saveCropperCoords = null;
      this.coordinates = null;
    }
  }

  cropperReady(event: any): void {
    if (this.photo.editWindowCoordinates) {
      this.cropperCoords = {
        x1: this.photo.editWindowCoordinates.xLeft,
        x2: this.photo.editWindowCoordinates.xRight,
        y1: this.photo.editWindowCoordinates.yTop,
        y2: this.photo.editWindowCoordinates.yBottom,
      };
    }
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedImage = event.base64;
    this.coordinates = {
      xLeft: event.imagePosition.x1,
      yTop: event.imagePosition.y1,
      xRight: event.imagePosition.x2,
      yBottom: event.imagePosition.y2,
    };
    this.saveCropperCoords = {
      xLeft: event.cropperPosition.x1,
      yTop: event.cropperPosition.y1,
      xRight: event.cropperPosition.x2,
      yBottom: event.cropperPosition.y2,
    };
    this.form.group.markAsDirty();
  }

  startUploading(): void {
    this.uploading = true;
  }

  finishUploading(): void {
    this.uploading = false;
  }
}
